import { useState } from "react"
import ReviewsList from "./reviewsList";
import AdminButton from "../../../core/admin/adminButton";
import AdminTextInput from "../../../core/admin/adminTextInput";
import AdminEmptyState from "../../../core/admin/adminEmptyState";
import { AdminSelect } from "../../../core/admin/adminField";
import { AdminSegmented } from "../../../core/admin/adminTabs";
import { AdminLoading } from "../../../core/admin/adminSpinner";
import { SearchLineIcon } from "../../../../img/svg/adminIcons";
import { collection, getDocs, query, Timestamp, where, WhereFilterOp } from "firebase/firestore";
import { db } from "../../../../firebase";
import AdminToast from "../../../core/admin/adminToast";
import { IRequest } from "./requests";

export interface IReview {
    id: string,
    rate: number,
    review: string,
    createdAt: Date,
    hidden: boolean,
    advertiser: string,
    userId: string,
    watch: string
}

export default function Reviews({ requests }: { requests: IRequest[] | null }) {

    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("rate");
    const [rate, setRate] = useState("1");
    const [advertiser, setAdvertiser] = useState("");
    const [day, setDay] = useState("");
    const [month, setMonth] = useState("");
    const [year, setYear] = useState("");
    const [reviews, setReviews] = useState<IReview[] | null>(null);
    const [feedback, setFeedback] = useState("");
    const [goodToast, setGoodToast] = useState(false);
    const [badToast, setBadToast] = useState(false);

    const operators: { [key: string]: WhereFilterOp } = {
        "==": "==",
        ">=": ">=",
        ">": ">",
        "<": "<",
        "<=": "<=",
    };

    const getReviews = async () => {
        try {
            setLoading(true);
            setFeedback("");
            let operator = "==";
            let docs: IReview[] = [];
            if (filter === "createdAt") {
                operator = ">=";
            }
            const q = query(collection(db, "reviews"), where(filter, operators[operator], handleFilterValue(filter)));
            const docsSnap = await getDocs(q);
            if (docsSnap.empty) {
                setLoading(false);
                setFeedback("Nenhum resultado encontrado");
                setReviews(null);
                return null;
            }
            docsSnap.forEach(document => {
                docs.push({
                    id: document.id,
                    rate: document.data().rate,
                    review: document.data().review,
                    createdAt: document.data().createdAt.toDate(),
                    hidden: document.data().hidden,
                    advertiser: document.data().advertiser,
                    userId: document.data().userId,
                    watch: document.data().watch,
                })
            })

            setReviews(docs);
            setLoading(false);
            return null;
        } catch (error) {
            console.log(error);
            setReviews(null);
            setLoading(false);
            return null;
        }
    }

    const handleFilterValue = (filter: string) => {
        if (filter === "rate") {
            return parseInt(rate);
        }
        if (filter === "advertiser") {
            return advertiser;
        }
        if (filter === "createdAt") {
            const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            const timestamp = Timestamp.fromDate(date);
            return timestamp;
        }
    }

    const handleDay = (value: string) => {
        if (value === "") {
            setDay("");
            return null;
        }
        if (!/^\d+$/.test(value)) {
            return null;
        }
        if (parseInt(value) > 31) {
            return null;
        }
        setDay(value);
    }

    const handleMonth = (value: string) => {
        if (value === "") {
            setMonth("");
            return null;
        }
        if (!/^\d+$/.test(value)) {
            return null;
        }
        if (parseInt(value) > 12) {
            return null;
        }
        setMonth(value);
    }

    const handleYear = (value: string) => {
        if (value === "") {
            setYear("");
            return null;
        }
        if (!/^\d+$/.test(value)) {
            return null;
        }
        if (parseInt(value) > 9999) {
            return null;
        }
        setYear(value);
    }

    return (
        <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-4 rounded-xl bg-gray-50 p-4 ring-1 ring-gray-200">
                <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-semibold text-gray-700">Filtrar por</span>
                    <AdminSegmented
                        value={filter}
                        onChange={setFilter}
                        options={[
                            { value: "rate", label: "Nota" },
                            { value: "advertiser", label: "ID Anunciante" },
                            { value: "createdAt", label: "Data" },
                        ]}
                    />
                </div>
                <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                    {
                        filter === "rate" ?
                            <AdminSelect className="sm:w-40" value={rate} onChange={e => setRate(e.target.value)}>
                                <option value="1">1 estrela</option>
                                <option value="2">2 estrelas</option>
                                <option value="3">3 estrelas</option>
                                <option value="4">4 estrelas</option>
                                <option value="5">5 estrelas</option>
                            </AdminSelect>
                            : filter === "advertiser" ?
                                <AdminTextInput className="sm:max-w-sm" value={advertiser} onChange={e => setAdvertiser(e.target.value)} onEnter={getReviews} placeholder="ID do anunciante" />
                                : filter === "createdAt" ?
                                    <div className="flex flex-col gap-1.5">
                                        <span className="text-xs font-semibold text-gray-500">A partir de:</span>
                                        <div className="flex gap-2">
                                            <AdminTextInput className="!w-16 text-center" inputMode="numeric" value={day} onChange={e => handleDay(e.target.value)} placeholder="dia" />
                                            <AdminTextInput className="!w-16 text-center" inputMode="numeric" value={month} onChange={e => handleMonth(e.target.value)} placeholder="mês" />
                                            <AdminTextInput className="!w-20 text-center" inputMode="numeric" value={year} onChange={e => handleYear(e.target.value)} onEnter={getReviews} placeholder="ano" />
                                        </div>
                                    </div>
                                    : null
                    }
                    <AdminButton icon={<SearchLineIcon width="16px" />} loading={loading} onClick={getReviews}>Listar</AdminButton>
                </div>
            </div>
            {
                feedback ?
                    <AdminEmptyState title={feedback} />
                    : null
            }
            {
                loading ?
                    <AdminLoading />
                    :
                    <ReviewsList requests={requests} reviews={reviews} refresh={getReviews} goodToast={() => setGoodToast(true)} badToast={() => setBadToast(true)} />
            }
            <AdminToast toast={goodToast} setToast={setGoodToast} message="Avaliação atualizada!" tone="success" />
            <AdminToast toast={badToast} setToast={setBadToast} message="Erro!" tone="danger" />
        </div>
    )
}
